import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, IconButton, List, ListItem, ListItemText,
    ListItemSecondaryAction, ListItemAvatar, Avatar, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { getFirestore, doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from "../../firebase";

const ViewHistory = ({theme}) => {
    const [movieHistory, setMovieHistory] = useState([]);
    const [tvShowHistory, setTvShowHistory] = useState([]);
    const auth = getAuth(app);
    const firestore = getFirestore(app);
    const navigate = useNavigate();

    const [deleteConfirmation, setDeleteConfirmation] = useState({
        open: false,
        entryToDelete: null
    });

    const [clearAllConfirmation, setClearAllConfirmation] = useState(false);

    const fetchSearchHistory = async () => {
        try {
            const user = auth.currentUser;
            if (!user) {
                console.error("No user is logged in.");
                return;
            }

            const userId = user.email; // Assuming user.email is used as the document ID
            const historyRef = doc(firestore, "history", userId);
            const historyDoc = await getDoc(historyRef);

            if (historyDoc.exists()) {
                const historyData = historyDoc.data();
                let movieHistoryData = [];
                let tvShowHistoryData = [];

                if (historyData.movies) {
                    movieHistoryData = historyData.movies.flatMap(entry =>
                        entry.timestamps.map(ts => ({
                            type: 'movie',
                            movieId: entry.movieId,
                            mediaTitle: entry.mediaTitle,
                            genres: entry.genres,
                            posterPath: entry.posterPath,
                            timestamp: ts.toDate()
                        }))
                    );
                }

                if (historyData.tvShows) {
                    tvShowHistoryData = historyData.tvShows.flatMap(entry =>
                        entry.timestamps.map(ts => ({
                            type: 'tv',
                            tvId: entry.tvId, // Assuming the TV show ID is stored in the entry
                            mediaTitle: entry.mediaTitle,
                            genres: entry.genres,
                            posterPath: entry.posterPath,
                            timestamp: ts.toDate()
                        }))
                    );
                }

                // Sort history by timestamp descending
                movieHistoryData.sort((a, b) => b.timestamp - a.timestamp);
                tvShowHistoryData.sort((a, b) => b.timestamp - a.timestamp);

                setMovieHistory(movieHistoryData);
                setTvShowHistory(tvShowHistoryData);
            } else {
                console.log("No search history found for the user.");
            }
        } catch (error) {
            console.error("Failed to fetch search history:", error);
        }
    };

    useEffect(() => {
        fetchSearchHistory();
    }, [auth, firestore]);

    const deleteHistoryEntry = async (type, index, id) => {
        try {
            const user = auth.currentUser;
            if (!user) {
                console.error("No user is logged in.");
                return;
            }

            const userId = user.email; // Assuming user.email is used as the document ID
            const historyRef = doc(firestore, "history", userId);
            const historyDoc = await getDoc(historyRef);
            const historyData = historyDoc.data();

            let updatedHistoryData = {};
            if (type === 'movie') {
                updatedHistoryData = {
                    ...historyData,
                    movies: historyData.movies.filter(movie => movie.movieId !== id)
                };
                setMovieHistory(prev => prev.filter((_, idx) => idx !== index));
            } else if (type === 'tv') {
                updatedHistoryData = {
                    ...historyData,
                    tvShows: historyData.tvShows.filter(tvShow => tvShow.tvId !== id)
                };
                setTvShowHistory(prev => prev.filter((_, idx) => idx !== index));
            }

            await updateDoc(historyRef, updatedHistoryData);
        } catch (error) {
            console.error("Failed to delete search history entry:", error);
        }
    };

    const clearAllHistory = async () => {
        try {
            const user = auth.currentUser;
            if (!user) {
                console.error("No user is logged in.");
                return;
            }

            const userId = user.email; // Assuming user.email is used as the document ID
            setMovieHistory([]);
            setTvShowHistory([]);

            const historyRef = doc(firestore, "history", userId);
            await deleteDoc(historyRef);  // Delete the entire document
        } catch (error) {
            console.error("Failed to clear search history:", error);
        }
    };

    const formatDate = (timestamp) => {
        try {
            return timestamp.toLocaleDateString(undefined, {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            });
        } catch (error) {
            console.error("Failed to format date:", error);
            return "Invalid date";
        }
    };

    const formatTime = (timestamp) => {
        try {
            return timestamp.toLocaleTimeString([], {
                hour: '2-digit', minute: '2-digit'
            });
        } catch (error) {
            console.error("Failed to format time:", error);
            return "Invalid time";
        }
    };

    const handleMovieClick = async (type, id) => {
        try {
            if (type === 'movie') {
                navigate(`/movie/${id}`);
            } else if (type === 'tv') {
                navigate(`/tv/${id}`); // Assuming tvId is correctly stored in the entry
            }
        } catch (error) {
            console.error("Failed to navigate to details:", error);
        }
    };

    const handleDeleteConfirmation = async (confirmed, type, index, id) => {
        if (confirmed) {
            await deleteHistoryEntry(type, index, id);
        }
        setDeleteConfirmation({ open: false, entryToDelete: null });
    };

    const handleClearAllConfirmation = async (confirmed) => {
        if (confirmed) {
            await clearAllHistory();
        }
        setClearAllConfirmation(false);
    };

    const groupByDate = (entries) => {
        const grouped = {};
        entries.forEach(entry => {
            const dateKey = formatDate(entry.timestamp);
            if (!grouped[dateKey]) {
                grouped[dateKey] = [];
            }
            grouped[dateKey].push(entry);
        });
        return grouped;
    };

    const groupedMovieHistory = groupByDate(movieHistory);
    const groupedTvShowHistory = groupByDate(tvShowHistory);

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '20px',
            backgroundColor: "white",
            minHeight: '100vh',
            maxWidth: '1450px',
            margin: '0 auto'
        }}>
            {/* Movie History */}
            <Box sx={{
                flex: 1,
                maxWidth: '600px',
                marginRight: '20px',
                display: { xs: 'none', md: 'block' } // Hide on small screens (mobile)
            }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#333' }}>
                    Movie History
                </Typography>
                {Object.keys(groupedMovieHistory).length === 0 ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', color: '#777' }}>
                        No movie history found.
                    </Typography>
                ) : (
                    <>
                        {Object.entries(groupedMovieHistory).map(([date, entries]) => (
                            <Box key={date} sx={{ marginBottom: '20px' }}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#333' }}>
                                    {date}
                                </Typography>
                                <List>
                                    {entries.map((entry, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                bgcolor: 'black',
                                                marginBottom: '10px',
                                                borderRadius: '8px',
                                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'scale(1.02)',
                                                    cursor: 'pointer'
                                                }
                                            }}
                                            onClick={() => handleMovieClick('movie', entry.movieId)}
                                        >
                                            <ListItemAvatar>
                                                <Avatar src={`https://image.tmdb.org/t/p/w500${entry.posterPath}`} alt={entry.mediaTitle} variant="rounded" />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={entry.mediaTitle}
                                                secondary={
                                                    <>
                                                        <Typography component="span" variant="body2" color="white">
                                                            {entry.genres.join(', ')}
                                                        </Typography>
                                                        <br />
                                                        {formatTime(entry.timestamp)}
                                                    </>
                                                }
                                                primaryTypographyProps={{ color: 'white' }}
                                                secondaryTypographyProps={{ color: 'white' }}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); setDeleteConfirmation({ open: true, entryToDelete: { type: 'movie', index, id: entry.movieId } }); }}>
                                                    <DeleteIcon style={{ color: 'white' }} />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        ))}
                    </>
                )}
            </Box>

            {/* TV Show History */}
            <Box sx={{ flex: 1, maxWidth: '600px', marginLeft: '20px', display: { xs: 'none', md: 'block' } }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#333' }}>
                    TV Show History
                </Typography>
                {Object.keys(groupedTvShowHistory).length === 0 ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', color: '#777' }}>
                        No TV show history found.
                    </Typography>
                ) : (
                    <>
                        {Object.entries(groupedTvShowHistory).map(([date, entries]) => (
                            <Box key={date} sx={{ marginBottom: '20px' }}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#333' }}>
                                    {date}
                                </Typography>
                                <List>
                                    {entries.map((entry, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                bgcolor: 'black',
                                                marginBottom: '10px',
                                                borderRadius: '8px',
                                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'scale(1.02)',
                                                    cursor: 'pointer'
                                                }
                                            }}
                                            onClick={() => handleMovieClick('tv', entry.tvId)}
                                        >
                                            <ListItemAvatar>
                                                <Avatar src={`https://image.tmdb.org/t/p/w500${entry.posterPath}`} alt={entry.mediaTitle} variant="rounded" />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={entry.mediaTitle}
                                                secondary={
                                                    <>
                                                        <Typography component="span" variant="body2" color="white">
                                                            {entry.genres.join(', ')}
                                                        </Typography>
                                                        <br />
                                                        {formatTime(entry.timestamp)}
                                                    </>
                                                }
                                                primaryTypographyProps={{ color: 'white' }}
                                                secondaryTypographyProps={{ color: 'white' }}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); setDeleteConfirmation({ open: true, entryToDelete: { type: 'tv', index, id: entry.tvId } }); }}>
                                                    <DeleteIcon style={{ color: 'white' }} />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        ))}
                    </>
                )}
            </Box>

            {/* Mobile Responsive Layout */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#333', textAlign: 'center' }}>
                    Movie History
                </Typography>
                {Object.keys(groupedMovieHistory).length === 0 ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', color: '#777' }}>
                        No movie history found.
                    </Typography>
                ) : (
                    <>
                        {Object.entries(groupedMovieHistory).map(([date, entries]) => (
                            <Box key={date} sx={{ marginBottom: '20px' }}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#333', textAlign: 'center' }}>
                                    {date}
                                </Typography>
                                <List>
                                    {entries.map((entry, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                bgcolor: 'black',
                                                marginBottom: '10px',
                                                borderRadius: '8px',
                                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'scale(1.02)',
                                                    cursor: 'pointer'
                                                }
                                            }}
                                            onClick={() => handleMovieClick('movie', entry.movieId)}
                                        >
                                            <ListItemAvatar>
                                                <Avatar src={`https://image.tmdb.org/t/p/w500${entry.posterPath}`} alt={entry.mediaTitle} variant="rounded" />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={entry.mediaTitle}
                                                secondary={
                                                    <>
                                                        <Typography component="span" variant="body2" color="white">
                                                            {entry.genres.join(', ')}
                                                        </Typography>
                                                        <br />
                                                        {formatTime(entry.timestamp)}
                                                    </>
                                                }
                                                primaryTypographyProps={{ color: 'white' }}
                                                secondaryTypographyProps={{ color: 'white' }}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); setDeleteConfirmation({ open: true, entryToDelete: { type: 'movie', index, id: entry.movieId } }); }}>
                                                    <DeleteIcon style={{ color: 'white' }} />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        ))}
                    </>
                )}

                <Typography variant="h4" gutterBottom sx={{ color: '#333', textAlign: 'center', marginTop: '40px' }}>
                    TV Show History
                </Typography>
                {Object.keys(groupedTvShowHistory).length === 0 ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', color: '#777' }}>
                        No TV show history found.
                    </Typography>
                ) : (
                    <>
                        {Object.entries(groupedTvShowHistory).map(([date, entries]) => (
                            <Box key={date} sx={{ marginBottom: '20px' }}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#333', textAlign: 'center' }}>
                                    {date}
                                </Typography>
                                <List>
                                    {entries.map((entry, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                bgcolor: 'black',
                                                marginBottom: '10px',
                                                borderRadius: '8px',
                                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'scale(1.02)',
                                                    cursor: 'pointer'
                                                }
                                            }}
                                            onClick={() => handleMovieClick('tv', entry.tvId)}
                                        >
                                            <ListItemAvatar>
                                                <Avatar src={`https://image.tmdb.org/t/p/w500${entry.posterPath}`} alt={entry.mediaTitle} variant="rounded" />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={entry.mediaTitle}
                                                secondary={
                                                    <>
                                                        <Typography component="span" variant="body2" color="white">
                                                            {entry.genres.join(', ')}
                                                        </Typography>
                                                        <br />
                                                        {formatTime(entry.timestamp)}
                                                    </>
                                                }
                                                primaryTypographyProps={{ color: 'white' }}
                                                secondaryTypographyProps={{ color: 'white' }}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); setDeleteConfirmation({ open: true, entryToDelete: { type: 'tv', index, id: entry.tvId } }); }}>
                                                    <DeleteIcon style={{ color: 'white' }} />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        ))}
                    </>
                )}
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmation.open}
                onClose={() => setDeleteConfirmation({ open: false, entryToDelete: null })}
            >
                <DialogTitle>Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this {deleteConfirmation.entryToDelete?.type === 'movie' ? 'movie' : 'TV show'} from your history?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmation({ open: false, entryToDelete: null })} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleDeleteConfirmation(true, deleteConfirmation.entryToDelete.type, deleteConfirmation.entryToDelete.index, deleteConfirmation.entryToDelete.id)}
                        color="primary"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Clear All Confirmation Dialog */}
            <Dialog
                open={clearAllConfirmation}
                onClose={() => setClearAllConfirmation(false)}
            >
                <DialogTitle>Clear All Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to clear all your search history?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClearAllConfirmation(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => handleClearAllConfirmation(true)} color="primary">
                        Clear All
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Clear All Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <Button variant="contained" color="primary" onClick={() => setClearAllConfirmation(true)} style={{ backgroundColor: "black", color: "white" }}>
                    Clear All History
                </Button>
            </Box>
        </Box>
    );
};

export default ViewHistory;
